import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from '../../../../store/mst';
import AvailableExportColumnsQuery from '../../../../store/mst/queries/routes/AvailableExportColumnsQuery';
import { ExportColumnGroup } from '../../../../store/mst/models';
import { logglyLogger } from '../../../../services/logglyLogger';
import { DEFAULT_UNSELECTED_COLUMN_KEYS, ExportColumn, ExportColumnGroupData, GROUP_ORDER } from '../ColumnSelector/types';
import { getExportColumnsPreference, setExportColumnsPreference } from '../helpers/exportColumnsPreference';

interface UseExportColumns {
  groups: ExportColumnGroupData[];
  selectedKeys: Set<string>;
  isLoading: boolean;
  toggleColumn: (key: string) => void;
  toggleGroup: (group: ExportColumnGroup) => void;
  isGroupChecked: (group: ExportColumnGroup) => boolean;
  isGroupIndeterminate: (group: ExportColumnGroup) => boolean;
  selectedColumns: string[];
  hasSelection: boolean;
  hasError: boolean;
}

const useExportColumns = (enabled: boolean): UseExportColumns => {
  const store = useStore();
  const userId = store.account.user?.id;

  const [columns, setColumns] = useState<ExportColumn[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const fetchColumns = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await store.model.queryAvailableExportColumns({}, AvailableExportColumnsQuery);
        if (cancelled) return;

        const catalog: ExportColumn[] = result.availableExportColumns.map((column) => ({
          key: column.key,
          label: column.label,
          group: column.group as ExportColumnGroup,
          isCustomField: !!column.isCustomField,
        }));
        setColumns(catalog);

        const catalogKeys = catalog.map((column) => column.key);
        const defaultSelectedKeys = catalogKeys.filter((key) => !DEFAULT_UNSELECTED_COLUMN_KEYS.has(key));
        const saved = getExportColumnsPreference(userId);
        const restored = saved ? saved.filter((key) => catalogKeys.includes(key)) : defaultSelectedKeys;
        setSelectedKeys(new Set(restored.length ? restored : defaultSelectedKeys));
      } catch (err) {
        if (!cancelled) {
          setHasError(true);
          logglyLogger.log({ error: 'Failed to load export columns', info: (err as Error)?.message });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchColumns();
    return () => {
      cancelled = true;
    };
  }, [enabled, userId, store]);

  const persist = useCallback(
    (next: Set<string>) => {
      setSelectedKeys(new Set(next));
      setExportColumnsPreference(userId, Array.from(next));
    },
    [userId],
  );

  const groups = useMemo<ExportColumnGroupData[]>(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      columns: columns.filter((column) => column.group === group),
    })).filter((groupData) => groupData.columns.length > 0);
  }, [columns]);

  const toggleColumn = useCallback(
    (key: string) => {
      const next = new Set(selectedKeys);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      persist(next);
    },
    [selectedKeys, persist],
  );

  const toggleGroup = useCallback(
    (group: ExportColumnGroup) => {
      const groupKeys = columns.filter((column) => column.group === group).map((column) => column.key);
      const allSelected = groupKeys.every((key) => selectedKeys.has(key));
      const next = new Set(selectedKeys);
      groupKeys.forEach((key) => (allSelected ? next.delete(key) : next.add(key)));
      persist(next);
    },
    [columns, selectedKeys, persist],
  );

  const isGroupChecked = useCallback(
    (group: ExportColumnGroup) => {
      const groupKeys = columns.filter((column) => column.group === group).map((column) => column.key);
      return groupKeys.length > 0 && groupKeys.every((key) => selectedKeys.has(key));
    },
    [columns, selectedKeys],
  );

  const isGroupIndeterminate = useCallback(
    (group: ExportColumnGroup) => {
      const groupKeys = columns.filter((column) => column.group === group).map((column) => column.key);
      const selectedCount = groupKeys.filter((key) => selectedKeys.has(key)).length;
      return selectedCount > 0 && selectedCount < groupKeys.length;
    },
    [columns, selectedKeys],
  );

  const selectedColumns = useMemo(() => columns.map((column) => column.key).filter((key) => selectedKeys.has(key)), [columns, selectedKeys]);

  return {
    groups,
    selectedKeys,
    isLoading,
    toggleColumn,
    toggleGroup,
    isGroupChecked,
    isGroupIndeterminate,
    selectedColumns,
    hasSelection: selectedKeys.size > 0,
    hasError,
  };
};

export default useExportColumns;
